import {Component, h, Host, Prop, State, Watch} from '@stencil/core';
import {SearchRequest} from "@modules/onBoarding/domain/SearchRequest";
import {loadSearchCompanyForm, searchCompany} from "@modules/onBoarding";
import {CompanyViewModel} from "@modules/onBoarding/presentation/viewModel/CompanyViewModel";
import {CompaniesViewModel} from "@modules/onBoarding/presentation/viewModel/CompaniesViewModel";
import {SearchFormViewModel} from "@modules/onBoarding/presentation/viewModel/SearchFormViewModel";

@Component({
    tag: 'search-company-form',
    styleUrl: 'search-company-form.css',
    scoped: true,
})
export class SearchCompanyForm {

    @Prop() serviceLoginApiKey = '';
    @Prop() serviceLoginUrl = '';
    @Prop() country = 'FR';

    private searchFormViewModel: SearchFormViewModel;

    private companiesViewModel: CompaniesViewModel;

    @State() vatNumber: string = '';
    @State() companyName: string = '';
    @State() hasSearchResults: boolean = false;

    private renderSearchCompanyButton() {
        return (
            <button class="btn bg-primary w-100 text-white mt-2"
                    id="searchCompanySubmit"
                    data-testid="onboarding.search-company-form.submit.searchCompany"
                    type="submit">
                {this.searchFormViewModel.submitLabel}
            </button>
        );
    }

    private renderSearchResultsButtons() {
        return (
            <div>
                {this.renderNotListedCompanyButton()}
                {this.renderConfirmSelectionButton()}
            </div>
        );
    }

    private renderNotListedCompanyButton() {
        return (
            <button class="btn btn-outline-light font-weight-regular w-100 text-primary mt-2"
                    id="companyNotListedSubmit"
                    data-testid="onboarding.search-company-form.submit.notListedCompany"
                    type="submit">{this.companiesViewModel.notListedButtonLabel}
            </button>
        );
    }

    private renderConfirmSelectionButton() {
        return (
            <button class="btn bg-primary text-white w-100 mt-2"
                    id="companySelectionSubmit"
                    data-testid="onboarding.search-company-form.submit.selectCompany"
                    type="submit">{this.companiesViewModel.confirmSelectionButtonLabel}
            </button>
        );
    }

    @Watch('hasSearchResults')
    private renderFormButtons() {
        if (this.hasSearchResults) {
            return this.renderSearchResultsButtons();
        }

        return this.renderSearchCompanyButton();
    }

    async componentWillLoad() {
        this.searchFormViewModel = await loadSearchCompanyForm.execute();
    }

    private handleChange(event: Event) {
        const input = event.target as HTMLInputElement;
        this[input.name] = input.value;
    }

    private async handleSubmit(e) {
        e.preventDefault();

        const request: SearchRequest = {
            vatNumber: this.vatNumber,
            companyName: this.companyName,
            country: this.country
        };

        this.companiesViewModel = await searchCompany.execute(request);
        this.hasSearchResults = true;
    }

    private renderCompaniesBlock() {
        return (
            <div class="mt-4">
                <span class="text-primary font-weight-bold">{this.companiesViewModel.selectTitle}</span>
                {this.renderCompanies()}
            </div>
        );
    }

    private renderCompanies() {
        return (
            this.companiesViewModel.companies.map((company) => {
                return this.renderCompany(company);
            })
        );
    }

    private renderCompany(company: CompanyViewModel) {
        return (
            <div class="custom-control custom-radio mt-3 company-select">
                <input type="radio" class="custom-control-input"
                       name="customerOnBoardingUser.companyBvdData"
                       value={company.companyName}
                       required
                />
                <label class="custom-control-label text_sm">{this.renderCompanyLabel(company)}</label>
            </div>
        )
    }

    private renderCompanyLabel(company: CompanyViewModel) {
        return (
            <div class="d-flex flex-column">
                <span>{company.companyName} - {company.id}</span>
                <span>{company.address}</span>
                <span>{company.city}</span>
            </div>
        );
    }

    render() {
        return (
            <Host>
                <form data-testid="onboarding.search-company-form" id="searchCompanyForm"
                      onSubmit={(e) => this.handleSubmit(e)}>

                    <h2 class="title_xs text-dark mb-4">{this.searchFormViewModel.title}</h2>

                    <div class="form-group">
                        <label class="text-primary font-weight-bold" htmlFor="vatNumber">{this.searchFormViewModel.vatNumber.label}</label>
                        <input type="text" class="form-control" name="vatNumber" id="vatNumber"
                               placeholder={this.searchFormViewModel.vatNumber.placeholder}
                               data-testid="onboarding.search-company-form.vatNumber"
                               onChange={(e) => this.handleChange(e)}
                        />
                    </div>
                    <div class="separator mt-2 mb-4">
                        <div class="separator--line bg-lyreco-light"></div>
                        <span class="separator--text">{this.searchFormViewModel.separatorLabel}</span>
                        <div class="separator--line bg-lyreco-light"></div>
                    </div>
                    <div class="form-group">
                        <label class="text-primary font-weight-bold"
                               htmlFor="companyName">{this.searchFormViewModel.companyName.label}</label>
                        <input type="text" class="form-control" name="companyName" id="companyName"
                               placeholder={this.searchFormViewModel.companyName.placeholder}
                               data-testid="onboarding.search-company-form.companyName"
                               onChange={(e) => this.handleChange(e)}
                        />
                    </div>
                    {this.hasSearchResults ? this.renderCompaniesBlock() : ''}
                    {this.renderFormButtons()}
                </form>
            </Host>
        );
    }
}
